import { useL } from "@/lib/useL";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Download, Loader2, Plus, RotateCcw, Share2, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FlipCard } from "@/components/FlipCard";
import { DeckShare } from "@/components/DeckShare";
import { FlashcardsTour } from "@/components/FlashcardsTour";
import { InfoTip } from "@/components/InfoTip";
import { SwipeArea, CardArrows } from "@/components/CardNav";

const download = async (url, L) => {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = "SUX1_cartes_de_revision.pdf";
    a.click();
  } catch {
    toast.error(L("Échec du téléchargement.", "Download failed."));
  }
};

// Study session: due cards first; "Je savais" / "À revoir" (spaced repetition).
export default function FlashcardStudy() {
  const L = useL();
  const { id } = useParams();
  const [deck, setDeck] = useState(null);
  const [queue, setQueue] = useState([]);
  const [flipped, setFlipped] = useState(false);
  const [done, setDone] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [pos, setPos] = useState(0);

  useEffect(() => {
    api.get(`/flashcards/${id}`).then(({ data }) => {
      setDeck(data);
      const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });
      setQueue(data.cards.filter((c) => c.due <= today));
    }).catch(() => setDeck(false));
  }, [id]);

  if (deck === false) return <div className="p-10" data-testid="flashcards-not-found">{L("Jeu de cartes introuvable.", "Deck not found.")} <Link to="/cartes" className="underline">{L("Mes cartes", "My cards")}</Link></div>;
  if (!deck) return null;
  const at = queue.length ? pos % queue.length : 0;
  const card = queue[at];
  const shuffle = () => {
    const q = [...queue];
    for (let i = q.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [q[i], q[j]] = [q[j], q[i]];
    }
    setQueue(q);
    setPos(0);
    setFlipped(false);
    toast.success(L("Cartes mélangées.", "Cards shuffled."));
  };
  const move = (d) => { setFlipped(false); setPos((at + d + queue.length) % queue.length); };

  const answer = async (known) => {
    try {
      await api.post(`/flashcards/${id}/review`, { card_id: card.id, known });
    } catch {
      toast.error(L("Réponse non enregistrée.", "Answer not saved."));
    }
    setFlipped(false);
    setDone((n) => n + 1);
    // "À revoir": the card comes back at the end of today's session.
    setQueue((q) => {
      const rest = [...q.slice(0, at), ...q.slice(at + 1)];
      return known ? rest : [...rest, q[at]];
    });
    if (!known && at === queue.length - 1) setPos(0);
  };

  const addMore = async () => {
    setAdding(true);
    try {
      const { data } = await api.post(`/flashcards/${id}/more`);
      const added = data.cards.slice(deck.cards.length);
      setDeck(data);
      setQueue((q) => [...q, ...added]);
      toast.success(L(`${added.length} nouvelle(s) carte(s) ajoutée(s).`, `${added.length} new card(s) added.`));
    } catch (err) {
      toast.error(err?.response?.status === 402 ? L("Il vous faut 1 crédit pour ajouter des cartes.", "You need 1 credit to add cards.") : err?.response?.data?.detail || L("L'ajout a échoué.", "Adding cards failed."));
    } finally {
      setAdding(false);
    }
  };

  const btn = "font-mono uppercase tracking-[0.16em] text-[11px] px-4 py-3 border border-[#002FA7] text-[#002FA7] dark:border-blue-400 dark:text-blue-300 hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-2";
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10" data-testid="flashcard-study-page">
      <div className="flex items-center justify-between gap-3">
        <Link to="/cartes" className="font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] dark:text-blue-300" data-testid="flashcard-back-link">← {L("Mes cartes", "My cards")}</Link>
        {card && <FlashcardsTour />}
      </div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight mt-3 mb-2" style={{ fontFamily: "Outfit" }}>{deck.title}</h1>
      <p className="text-sm text-muted-foreground mb-6" data-testid="flashcard-progress">
        <InfoTip testid="flashcard-progress-info" content={L(<><b>Révision espacée</b> : seules les cartes prévues pour aujourd'hui s'affichent. « Je savais » la repousse (demain, puis 3 jours, puis 1 semaine). « À revoir » la remet à la fin de la séance du jour.</>, <><b>Spaced repetition</b>: only cards due today are shown. « I knew it » pushes it back (tomorrow, then 3 days, then 1 week). « Review again » puts it at the end of today's session.</>)} />{" "}
        {card ? L(`${queue.length} carte(s) à revoir · ${done} révisée(s) · ${deck.cards.length} au total`, `${queue.length} card(s) to review · ${done} reviewed · ${deck.cards.length} in total`) : L(`Bravo, révision terminée (${done} carte(s)) ! Les prochaines reviendront demain, dans 3 jours, puis dans 1 semaine.`, `Well done, review complete (${done} card(s))! Next ones come back tomorrow, in 3 days, then in 1 week.`)}
      </p>
      {card ? (
        <>
          <SwipeArea onPrev={() => move(-1)} onNext={() => move(1)}>
            <FlipCard q={card.q} a={card.a} flipped={flipped} onFlip={() => setFlipped((f) => !f)} />
          </SwipeArea>
          <CardArrows pos={at + 1} total={queue.length} onPrev={() => move(-1)} onNext={() => move(1)}
            prevDisabled={queue.length < 2} nextDisabled={queue.length < 2} testid="flashcard-nav" />
          {queue.length > 1 && (
            <div className="flex justify-center items-center gap-1 mt-3">
              <button type="button" onClick={shuffle} data-testid="flashcard-shuffle-btn"
                className="font-mono uppercase tracking-[0.16em] text-[11px] px-4 py-2 border border-[#002FA7] text-[#002FA7] dark:border-blue-400 dark:text-blue-300 hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-2">
                <Shuffle className="w-3.5 h-3.5" /> {L("Mélanger les cartes", "Shuffle cards")}
              </button>
              <InfoTip testid="flashcard-shuffle-info" content={L("Change l'ordre des cartes du jour, pour ne pas apprendre les réponses dans le même ordre. Glissez la carte ou utilisez les flèches pour passer à la suivante.", "Changes the order of today's cards so you don't learn answers in sequence. Swipe the card or use the arrows to move on.")} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button type="button" onClick={() => answer(false)} disabled={!flipped} data-testid="flashcard-again-btn"
              className="py-4 border-2 border-[#FF2A00] text-[#FF2A00] font-bold hover:bg-[#FF2A00] hover:text-white transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
              <RotateCcw className="w-4 h-4" /> {L("À revoir", "Review again")}
            </button>
            <button type="button" onClick={() => answer(true)} disabled={!flipped} data-testid="flashcard-known-btn"
              className="py-4 border-2 border-[#0a7a2f] text-[#0a7a2f] font-bold hover:bg-[#0a7a2f] hover:text-white transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
              <Check className="w-4 h-4" /> {L("Je savais", "I knew it")}
            </button>
          </div>
          {!flipped && <p className="text-xs text-muted-foreground mt-2 text-center">{L("Retournez la carte avant de répondre.", "Flip the card before answering.")}</p>}
        </>
      ) : (
        <button type="button" className={btn} onClick={() => { setQueue(deck.cards); setDone(0); setPos(0); }} data-testid="flashcard-review-all-btn">
          <RotateCcw className="w-3.5 h-3.5" /> {L("Tout réviser quand même", "Review all anyway")}
        </button>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-border">
        <button type="button" className={btn} onClick={() => download(`/flashcards/${id}/pdf`, L)} data-testid="flashcard-pdf-btn"><Download className="w-3.5 h-3.5" /> {L("Imprimer (PDF à découper)", "Print (cut-out PDF)")}</button>
        <button type="button" className={btn} onClick={addMore} disabled={adding} data-testid="flashcard-add-more-btn">
          {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {adding ? L("Ajout…", "Adding…") : L("+ 10 cartes · 1 crédit", "+ 10 cards · 1 credit")}
        </button>
        <span className="inline-flex items-center gap-1">
        <button type="button" className={btn} onClick={() => setShareOpen(true)} data-testid="flashcard-share-btn"><Share2 className="w-3.5 h-3.5" /> {L("Partager avec la classe", "Share with the class")}</button>
        <InfoTip testid="flashcard-tools-info" content={L(<><b>Imprimer</b> : PDF à découper, gratuit. <b>+ 10 cartes</b> : nouvelles questions tirées du même texte, sans doublon (1 crédit). <b>Partager</b> : lien et QR code pour que les élèves révisent sur leur téléphone, sans compte.</>, <><b>Print</b>: cut-out PDF, free. <b>+ 10 cards</b>: new questions from the same text, no duplicates (1 credit). <b>Share</b>: link and QR code so pupils review on their phone, no account needed.</>)} />
        </span>
      </div>
      <DeckShare deck={deck} open={shareOpen} onOpenChange={setShareOpen} onChange={(p) => setDeck({ ...deck, is_public: p })} />
    </div>
  );
}
